import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { pmsFrom } from '@/integrations/pms';
import { newsletterSchema, type NewsletterFormData } from '@/lib/validations/formSchemas';
import { cn } from '@/lib/utils';

interface NewsletterFormProps {
  className?: string;
  variant?: 'inline' | 'stacked';
  source?: string;
}

export const NewsletterForm: React.FC<NewsletterFormProps> = ({
  className,
  variant = 'inline',
  source = 'website',
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data: NewsletterFormData) => {
    setServerError(null);
    
    try {
      const { error } = await pmsFrom('newsletter_subscribers').insert({
        email: data.email,
        source,
      });

      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation - already subscribed
          setIsSubmitted(true);
          reset();
          return;
        }
        throw error;
      }

      setIsSubmitted(true);
      reset();

      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Newsletter form error:', err);
      setServerError('Something went wrong. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn('flex items-center gap-2 text-green-600 dark:text-green-400', className)}
      >
        <CheckCircle className="h-5 w-5" />
        <span className="font-medium">You're subscribed!</span>
      </motion.div>
    );
  }

  const isInline = variant === 'inline';

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn(
        isInline ? 'flex gap-2' : 'space-y-3',
        className
      )}
    >
      <div className={cn(isInline ? 'flex-1' : 'w-full')}>
        <Input
          type="email"
          placeholder="Enter your email"
          {...register('email')}
          aria-invalid={!!errors.email}
          className={cn(
            errors.email ? 'border-destructive' : '',
            isInline ? '' : 'w-full'
          )}
        />
        {errors.email && !isInline && (
          <p className="text-xs text-destructive mt-1">{errors.email.message}</p>
        )}
      </div>

      <Button type="submit" disabled={isSubmitting} className={cn(isInline ? '' : 'w-full')}>
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Mail className="h-4 w-4 mr-2" />
            Subscribe
          </>
        )}
      </Button>

      <AnimatePresence>
        {serverError && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(
              'text-xs text-destructive',
              isInline ? 'absolute -bottom-5 left-0' : ''
            )}
          >
            {serverError}
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
};
